import React, { useContext } from "react";
import { useIntl } from "react-intl";
import { useMatch, useNavigate, useSearchParams } from "react-router-dom";
import { Context as AuthContext } from "context/Auth";
import { useLoginDialog } from "context/LoginDialog";
import { useProfileChoiceDialog } from "context/ProfileChoiceDialog";
import { Dialog, DialogDescription, DialogTitle } from "components/ui/dialog";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { ROUTES } from "routes";
import { DialogCard } from "components/ui/dialog-card";
import LoginForm from "./LoginForm";

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null;

const LoginDialog: React.FC = () => {
    const { state: { open, redirectAfterLogin }, actions: { closeLogin } } = useLoginDialog();
    const { openProfileChoice } = useProfileChoiceDialog();
    const authData = useContext(AuthContext);
    const intl = useIntl();
    const navigate = useNavigate();
    const isLoginRoute = useMatch(ROUTES.LOGIN);
    const [searchParams] = useSearchParams();

    const routeRedirect = isLoginRoute
        ? (searchParams.get("redirect") ?? searchParams.get("returnUrl") ?? undefined)
        : undefined;
    const effectiveRedirect = redirectAfterLogin ?? routeRedirect;
    const isOpen = open || !!isLoginRoute;

    const handleOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            closeLogin();
            if (isLoginRoute) navigate(ROUTES.HOME, { replace: true });
        }
    };

    const handleOpenProfileChoice = () => {
        closeLogin();
        openProfileChoice(effectiveRedirect);
        if (isLoginRoute) navigate(ROUTES.HOME, { replace: true });
    };

    const onSubmit = async (values: { username: string; password: string; rememberMe?: boolean }) => {
        try {
            // login() hard-reloads to the post-auth URL; do not soft-navigate here
            // (that raced with location.href and could land on /connexion).
            await authData.login(values, effectiveRedirect);
            closeLogin();
        } catch (err: unknown) {
            if (isRecord(err) && "response" in err && isRecord(err.response) && "data" in err.response) {
                const responseData = err.response.data;
                if (isRecord(responseData) && "data" in responseData) {
                    return formatErrorResponse({ data: responseData.data });
                }
            }
            throw err;
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogCard closeLabel={intl.formatMessage({ id: "authentication.profile_choice.close" })}>
                <div className="flex flex-col">
                    <DialogTitle className="font-nunito text-3xl font-black leading-12 text-gray-900">
                        {intl.formatMessage({ id: "authentication.login.dialog.title" })}
                    </DialogTitle>
                    <DialogDescription className="font-nunito text-base font-medium leading-6 text-slate-600">
                        {intl.formatMessage({ id: "authentication.login.dialog.description" })}
                    </DialogDescription>
                </div>
                <LoginForm
                    onSubmit={onSubmit}
                    closeLogin={closeLogin}
                    onOpenProfileChoice={handleOpenProfileChoice}
                />
            </DialogCard>
        </Dialog>
    );
};

export default LoginDialog;
